import { Modal } from 'reactstrap';

import { useIsPhone } from 'src/shared/hooks/useMediaQuery';
import BookingSingle from './booking/BookingSingle';
import BookingWizard from './booking/BookingWizard';
import useBookingForm from './booking/useBookingForm';
import type { Appointment } from './types';

const BOOKING_TITLE_ID = 'booking-title';

interface QuickAppointmentModalProps {
  isOpen: boolean;
  toggle: () => void;
  timeZone?: string;
  currency?: string;
  /** Whether this user may book outside opening hours or in the past. */
  mayOverride: boolean;
  /** Preselected day, so opening from a calendar cell lands on that date. */
  defaultDate?: string;
  /** Preselected client, so booking from her profile skips the search. */
  defaultClientId?: number;
  onBooked?: (appointment: Appointment) => void;
}

/**
 * The booking flow: step by step on a phone, one screen on anything wider.
 *
 * Both are the same form (`useBookingForm`); only the layout differs. Full
 * screen on a phone, and a stray tap outside never throws away a half-built
 * booking.
 */
export default function QuickAppointmentModal({ isOpen, ...rest }: QuickAppointmentModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      toggle={rest.toggle}
      size="lg"
      fullscreen="sm"
      scrollable
      centered
      backdrop="static"
      modalClassName="v-modal v-booking"
      labelledBy={BOOKING_TITLE_ID}
    >
      {/* The form is mounted only while the modal is open, so its state starts
          empty every time rather than being cleared by an effect. The second
          booking of the afternoon must not inherit the first one's client. */}
      {isOpen && <Booking {...rest} />}
    </Modal>
  );
}

type BookingProps = Omit<QuickAppointmentModalProps, 'isOpen'>;

function Booking({
  toggle,
  timeZone,
  currency,
  mayOverride,
  defaultDate,
  defaultClientId,
  onBooked,
}: BookingProps) {
  const isPhone = useIsPhone();
  const form = useBookingForm({ timeZone, defaultDate, defaultClientId });

  const book = () =>
    form.submit((appointment) => {
      onBooked?.(appointment);
      toggle();
    });

  const props = {
    form,
    titleId: BOOKING_TITLE_ID,
    onClose: toggle,
    onBook: book,
    timeZone,
    currency,
    mayOverride,
  };

  return isPhone ? <BookingWizard {...props} /> : <BookingSingle {...props} />;
}
